import { View } from 'react-native';
import { PetStatus } from 'shared/@generated/models';
import { Text } from 'shared/components/text';
import { getPetStatusDotClassName, getPetStatusTextClassName } from 'shared/library/pet-status';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationScale } from './illustration-scene';
import {
  Caption,
  IllustrationFrame,
  Photo,
  useIllustrationCopy,
  type IllustrationProps,
} from './illustration-parts';

function ReservationStatusPill({
  s,
  label,
  reserved = false,
}: {
  s: IllustrationScale;
  label: string;
  reserved?: boolean;
}) {
  const pet = {
    status: reserved ? PetStatus.Reserved : PetStatus.Available,
    deleted_at: null,
    deceased: false,
  };

  return (
    <View
      className="border-brand-brown-dark/15 flex-row items-center self-start rounded-full"
      style={{
        height: s(11.8),
        paddingHorizontal: s(3.2),
        borderWidth: s(0.53),
        backgroundColor: palette.paper,
      }}
    >
      <View
        className={`rounded-full ${getPetStatusDotClassName(pet)}`}
        style={{ width: s(3.2), height: s(3.2), marginRight: s(3.2) }}
      />
      <Text
        className={`font-semibold ${getPetStatusTextClassName(pet)}`}
        numberOfLines={1}
        style={{ fontSize: s(6.4), lineHeight: s(8.53) }}
      >
        {label}
      </Text>
    </View>
  );
}

export function Reservation({ transparent = false }: IllustrationProps) {
  const t = useIllustrationCopy();
  return (
    <IllustrationFrame transparent={transparent}>
      {(s) => (
        <>
          <View
            className="absolute overflow-hidden"
            style={{
              left: s(16),
              top: s(7.5),
              width: s(128),
              height: s(46),
              borderWidth: s(0.9),
              borderColor: palette.ink + '70',
              borderRadius: s(6),
              backgroundColor: palette.paper,
            }}
          >
            <View className="flex-row items-center" style={{ padding: s(5), gap: s(7) }}>
              <Photo s={s} width={33} height={33} tone="olive" />
              <View className="flex-1" style={{ gap: s(1) }}>
                <Caption s={s} size={8}>
                  {t('second_pet')}
                </Caption>
                <Caption s={s} size={6} muted>
                  {t('breed_ragdoll')}
                </Caption>
                <ReservationStatusPill s={s} label={t('available')} />
              </View>
            </View>
          </View>
          <View
            className="absolute overflow-hidden"
            style={{
              left: s(6),
              top: s(57),
              width: s(148),
              height: s(46),
              borderWidth: s(0.9),
              borderColor: palette.ink + '90',
              borderRadius: s(6),
              backgroundColor: palette.paper,
            }}
          >
            <View className="flex-row items-center" style={{ padding: s(5), gap: s(7) }}>
              <Photo s={s} width={33} height={33} tone="clay" />
              <View className="flex-1" style={{ gap: s(1) }}>
                <Caption s={s} size={8}>
                  {t('pet')}
                </Caption>
                <Caption s={s} size={6} muted>
                  {t('breed')}
                </Caption>
                <ReservationStatusPill s={s} label={t('reserved')} reserved />
              </View>
            </View>
          </View>
          <View
            className="absolute overflow-hidden"
            style={{
              left: s(16),
              top: s(106.5),
              width: s(128),
              height: s(46),
              borderWidth: s(0.9),
              borderColor: palette.ink + '70',
              borderRadius: s(6),
              backgroundColor: palette.paper,
            }}
          >
            <View className="flex-row items-center" style={{ padding: s(5), gap: s(7) }}>
              <Photo s={s} width={33} height={33} tone="forest" />
              <View className="flex-1" style={{ gap: s(1) }}>
                <Caption s={s} size={8}>
                  {t('pet_three')}
                </Caption>
                <Caption s={s} size={6} muted>
                  {t('breed_labrador')}
                </Caption>
                <ReservationStatusPill s={s} label={t('available')} />
              </View>
            </View>
          </View>
        </>
      )}
    </IllustrationFrame>
  );
}
